import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { COMPANY } from "@/lib/company";
import { clientInfo, getGateToken, setGateToken, clearGateToken } from "@/lib/clientInfo";
import { BrandBackdrop, BrandStrip } from "@/components/login/BrandBackdrop";
import { PinPad } from "@/components/login/PinPad";
import { BannedScreen } from "@/components/login/BannedScreen";
import { Zap, Loader2, Shield, HardHat, ArrowLeft, KeyRound, Lock, LockKeyhole, ShieldCheck, ChevronRight, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const darkInput = "h-12 text-base bg-white/[0.05] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#F55913] focus-visible:ring-offset-0";
const roleLabel = (r) => (r === "admin" ? "Yönetici" : "Depo Personeli");
const Avatar = ({ u, big }) => (
  <div className={cn("rounded-xl flex items-center justify-center shrink-0", big ? "h-12 w-12" : "h-10 w-10", u.role === "admin" ? "bg-[#F55913]/15 text-[#FF8A4C]" : "bg-sky-500/15 text-sky-300")}>
    {u.role === "admin" ? <Shield className="h-5 w-5" /> : <HardHat className="h-5 w-5" />}
  </div>
);

// Kalan deneme hakkı göstergesi
const Attempts = ({ left, max, msg, testid }) => (
  <div className="flex flex-col items-center gap-2" data-testid={testid}>
    <div className="flex gap-1.5">{Array.from({ length: max }).map((_, i) => <span key={i} className={cn("h-1.5 w-8 rounded-full transition-colors", i < left ? "bg-emerald-400/80" : "bg-red-500")} />)}</div>
    <p className={cn("text-xs", msg ? "text-red-300 font-semibold" : "text-white/45")} data-testid={`${testid}-text`}>{msg || `${left} deneme hakkınız var`}</p>
  </div>
);

export default function Login() {
  const { userLogin, pinLogin, login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(() => (getGateToken() ? "users" : "check"));   // check | gate | banned | users | cred | email
  const [gate, setGate] = useState({ code: "", left: 3, max: 3, shake: 0, msg: "" });
  const [ban, setBan] = useState(null);
  const [credMode, setCredMode] = useState("password");
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [pinShake, setPinShake] = useState(0);
  const [credMsg, setCredMsg] = useState("");
  const [email, setEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [loading, setLoading] = useState(false);
  const pwRef = useRef(null);
  const selectedUser = users.find((u) => u.id === selectedId);

  // 423 = engellendi · 401 gate_required = giriş parolası süresi doldu · diğerleri: mesaj
  const authErr = (err, fallback) => {
    const st = err?.response?.status;
    const d = err?.response?.data?.detail;
    if (st === 423) { clearGateToken(); setBan(d || {}); setStep("banned"); return; }
    if (st === 401 && d?.code === "gate_required") { clearGateToken(); setStep("gate"); toast.error("Giriş parolası süresi doldu — tekrar girin"); return; }
    if (st === 401 && d?.code === "login_wrong") { setCredMsg(d.message); toast.error(d.message); return; }
    toast.error(apiError(err, fallback));
  };
  const loadUsers = () => api.get("/auth/login-users").then((r) => setUsers(r.data)).catch((e) => authErr(e, "Kullanıcılar alınamadı"));
  const checkGate = () => api.get("/auth/gate/status")
    .then(({ data }) => {
      if (data.banned) { setBan({ ip: data.ip, at: data.banned_at }); setStep("banned"); }
      else { setGate((g) => ({ ...g, left: data.remaining, max: data.max, msg: "" })); setStep("gate"); }
    })
    .catch(() => setStep("gate"));

  useEffect(() => {
    clientInfo();
    if (step === "check") checkGate(); else if (step === "users") loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (step === "cred" && credMode === "password") { const t = setTimeout(() => pwRef.current?.focus(), 120); return () => clearTimeout(t); }
    return undefined;
  }, [step, credMode, selectedId]);

  const submitGate = async (code) => {
    if (code.length < 4 || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/gate", { code, client: await clientInfo() });
      setGateToken(data.gate_token, data.expires_in);
      setGate((g) => ({ ...g, code: "", msg: "" }));
      setStep("users");
      loadUsers();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 401 && d?.code === "gate_wrong") setGate((g) => ({ ...g, code: "", left: d.remaining, msg: d.message, shake: g.shake + 1 }));
      else { setGate((g) => ({ ...g, code: "" })); authErr(err, "Doğrulanamadı"); }
    } finally { setLoading(false); }
  };

  const pickUser = (u) => { setSelectedId(u.id); setPassword(""); setPin(""); setCredMsg(""); setCredMode(u.has_pin ? "pin" : "password"); setStep("cred"); };
  const backToUsers = () => { setStep("users"); setSelectedId(""); setPassword(""); setPin(""); };
  const done = (u) => { toast.success(`Hoş geldiniz, ${u.name}`); navigate("/"); };

  const submitPassword = async () => {
    if (!password) return toast.error("Şifre girin");
    setLoading(true);
    try { done(await userLogin(selectedId, password)); } catch (err) { authErr(err, "Şifre hatalı"); setPassword(""); } finally { setLoading(false); }
  };
  const submitPin = async (val) => {
    if (val.length < 4 || loading) return;
    setLoading(true);
    try { done(await pinLogin(selectedId, val)); } catch (err) { authErr(err, "PIN hatalı"); setPin(""); setPinShake((s) => s + 1); } finally { setLoading(false); }
  };
  const submitEmail = async (e) => {
    e.preventDefault(); setLoading(true);
    try { done(await login(email, emailPw)); } catch (err) { authErr(err, "Giriş başarısız"); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[100dvh] relative flex flex-col items-center justify-center px-4 py-10 text-white overflow-hidden" data-testid="login-page">
      <BrandBackdrop />
      <div className="relative w-full max-w-[420px]">
        <div className="flex items-center justify-center gap-3 mb-7">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#F55913] to-[#C2410C] grid place-items-center shadow-lg shadow-orange-600/30"><Zap className="h-7 w-7 text-white" /></div>
          <div className="leading-tight">
            <div className="font-head font-black text-xl tracking-tight">GÜMÜŞALAN</div>
            <div className="text-[10px] uppercase tracking-[0.42em] text-[#FF8A4C] font-bold">Elektrik · Panel</div>
          </div>
        </div>

        <div className="rounded-[28px] border border-white/10 bg-[#0D1322]/75 backdrop-blur-2xl shadow-[0_40px_100px_-30px_rgba(0,0,0,.9)] p-6 sm:p-8 stagger-in" data-testid="login-form">
          {step === "check" && <div className="py-16 grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-white/50" /></div>}

          {step === "banned" && <BannedScreen ban={ban} onRetry={() => { setStep("check"); checkGate(); }} />}

          {step === "gate" && (
            <div className="space-y-7" data-testid="login-gate">
              <div className="text-center">
                <div className="mx-auto h-14 w-14 rounded-2xl grid place-items-center bg-white/[0.06] ring-1 ring-white/10"><LockKeyhole className="h-7 w-7 text-[#FF8A4C]" /></div>
                <h1 className="font-head text-2xl font-black tracking-tight mt-4">Güvenli Giriş</h1>
                <p className="text-sm text-white/55 mt-1">Panele erişmek için 4 haneli giriş parolasını girin</p>
              </div>
              <PinPad value={gate.code} onChange={(v) => setGate((g) => ({ ...g, code: v }))} onComplete={submitGate} prefix="gate" submitTestid="gate-submit" loading={loading} shakeKey={gate.shake} />
              <Attempts left={gate.left} max={gate.max} msg={gate.msg} testid="gate-remaining" />
            </div>
          )}

          {step === "users" && (
            <div className="space-y-5" data-testid="login-users">
              <div className="text-center">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-300 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider"><ShieldCheck className="h-3.5 w-3.5" />Parola doğrulandı</div>
                <h2 className="font-head text-2xl font-black tracking-tight mt-3">Kullanıcı Seçin</h2>
                <p className="text-white/50 text-sm mt-1">Devam etmek için hesabınızı seçin</p>
              </div>
              <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto pr-1 -mr-1">
                {users.length === 0 ? <p className="text-sm text-white/45 text-center py-6">Kullanıcılar yükleniyor…</p> : users.map((u) => (
                  <button key={u.id} type="button" onClick={() => pickUser(u)} data-testid={`login-user-${u.id}`}
                    className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-3 text-left transition-colors duration-150 hover:bg-white/[0.08] hover:border-[#F55913]/50">
                    <Avatar u={u} />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{u.name}</div>
                      <div className="text-[11px] text-white/45">{roleLabel(u.role)}{u.has_pin ? " · PIN" : ""}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-white/35 transition-transform group-hover:translate-x-0.5" />
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setStep("email")} className="text-xs text-white/45 hover:text-white w-full text-center inline-flex items-center justify-center gap-1.5" data-testid="switch-email"><Mail className="h-3.5 w-3.5" />E-posta ile giriş</button>
            </div>
          )}

          {step === "cred" && selectedUser && (
            <div className="space-y-6">
              <button type="button" onClick={backToUsers} className="text-xs text-white/50 hover:text-white flex items-center gap-1" data-testid="login-back"><ArrowLeft className="h-3.5 w-3.5" />Kullanıcı değiştir</button>
              <div className="flex items-center gap-3">
                <Avatar u={selectedUser} big />
                <div><div className="font-head font-bold text-lg leading-tight">{selectedUser.name}</div><div className="text-xs text-white/45">{roleLabel(selectedUser.role)}</div></div>
              </div>
              {credMode === "password" ? (
                <form onSubmit={(e) => { e.preventDefault(); submitPassword(); }} className="space-y-4">
                  <label className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-white/50 flex items-center gap-1"><Lock className="h-3 w-3" />Şifre</label>
                  <Input ref={pwRef} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={darkInput} data-testid="login-user-password" autoComplete="current-password" />
                  <button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-gradient-to-br from-[#F55913] to-[#D9480F] font-bold shadow-lg shadow-orange-600/25 active:scale-[0.99] transition-transform disabled:opacity-50 grid place-items-center" data-testid="user-login-submit">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Giriş Yap"}
                  </button>
                  {selectedUser.has_pin && <button type="button" onClick={() => setCredMode("pin")} className="text-xs text-white/45 hover:text-white w-full text-center flex items-center justify-center gap-1" data-testid="use-pin"><KeyRound className="h-3 w-3" />PIN ile giriş yap</button>}
                </form>
              ) : (
                <div className="space-y-5">
                  <p className="text-center text-xs text-white/50">4 haneli PIN'inizi girin (tuş takımı veya klavye)</p>
                  <PinPad value={pin} onChange={setPin} onComplete={submitPin} prefix="pin" submitTestid="pin-login-submit" loading={loading} shakeKey={pinShake} />
                  <button type="button" onClick={() => setCredMode("password")} className="text-xs text-white/45 hover:text-white w-full text-center flex items-center justify-center gap-1" data-testid="use-password"><Lock className="h-3 w-3" />Şifre ile giriş yap</button>
                </div>
              )}
              {credMsg && <p className="text-center text-xs font-semibold text-red-300" data-testid="login-cred-error">{credMsg}</p>}
            </div>
          )}

          {step === "email" && (
            <form onSubmit={submitEmail} className="space-y-5">
              <div className="text-center">
                <h2 className="font-head text-2xl font-black tracking-tight">E-posta ile Giriş</h2>
                <p className="text-white/50 text-sm mt-1">E-posta ve şifrenizle oturum açın</p>
              </div>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ornek@gumusalan.com" data-testid="login-email" className={darkInput} autoComplete="username" />
              <Input id="password" type="password" required value={emailPw} onChange={(e) => setEmailPw(e.target.value)} placeholder="Şifre" data-testid="login-password" className={darkInput} autoComplete="current-password" />
              <button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-gradient-to-br from-[#F55913] to-[#D9480F] font-bold shadow-lg shadow-orange-600/25 disabled:opacity-50 grid place-items-center" data-testid="login-submit">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Giriş Yap"}
              </button>
              {credMsg && <p className="text-center text-xs font-semibold text-red-300" data-testid="login-email-error">{credMsg}</p>}
              <button type="button" onClick={() => setStep("users")} className="text-xs text-white/45 hover:text-white w-full text-center flex items-center justify-center gap-1" data-testid="switch-pin"><ArrowLeft className="h-3 w-3" />Kullanıcı seçerek giriş</button>
            </form>
          )}
        </div>

        <div className="mt-5 flex items-center justify-center gap-1.5 text-[10.5px] text-white/40" data-testid="login-security-note"><ShieldCheck className="h-3.5 w-3.5" />Şifreli bağlantı · Tüm girişler cihaz ve konum bilgisiyle kayıt altına alınır</div>
      </div>
      <BrandStrip />
      <div className="relative mt-6 text-[10px] text-white/30 uppercase tracking-[0.2em]">© {new Date().getFullYear()} {COMPANY.name}</div>
    </div>
  );
}
